import { api } from '@/lib/axios';

export enum CalendarEventType {
  MEETING = 'MEETING',
  APPOINTMENT = 'APPOINTMENT',
  TASK = 'TASK',
  REMINDER = 'REMINDER',
  BREAK = 'BREAK',
  OTHER = 'OTHER',
}

export enum CalendarEventStatus {
  SCHEDULED = 'SCHEDULED',
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  NO_SHOW = 'NO_SHOW',
}

export enum CalendarEventRecurrence {
  NONE = 'NONE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  CUSTOM = 'CUSTOM',
}

export interface Attendee {
  email: string;
  name?: string;
  status?: 'pending' | 'accepted' | 'declined' | 'tentative';
}

export interface Reminder {
  type: 'email' | 'notification';
  minutes: number;
}

export interface Attachment {
  name: string;
  url: string;
  type?: string;
}

export interface Calendar {
  id: string;
  userId: string;
  name: string;
  description?: string;
  color: string;
  timezone: string;
  active: boolean;
  googleCalendarId?: string;
  appleCalendarId?: string;
  lastSyncAt?: string;
  syncEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    events: number;
  };
}

export interface CalendarEvent {
  id: string;
  calendarId: string;
  userId: string;
  title: string;
  description?: string;
  location?: string;
  type: CalendarEventType;
  status: CalendarEventStatus;
  startDate: string;
  endDate: string;
  allDay: boolean;
  timezone: string;
  recurrence: CalendarEventRecurrence;
  recurrenceRule?: string;
  recurrenceEnd?: string;
  attendees?: Attendee[];
  reminders?: Reminder[];
  meetingUrl?: string;
  attachments?: Attachment[];
  googleEventId?: string;
  appleEventId?: string;
  lastSyncAt?: string;
  color?: string;
  priority: number;
  isPrivate: boolean;
  clientId?: string;
  orderId?: string;
  reservationId?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  calendar?: Calendar;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateCalendarDto {
  name?: string;
  description?: string;
  color?: string;
  timezone?: string;
  active?: boolean;
}

export interface UpdateCalendarDto {
  name?: string;
  description?: string;
  color?: string;
  timezone?: string;
  active?: boolean;
  syncEnabled?: boolean;
}

export interface CreateCalendarEventDto {
  calendarId: string;
  title: string;
  description?: string;
  location?: string;
  type?: CalendarEventType;
  status?: CalendarEventStatus;
  startDate: string;
  endDate: string;
  allDay?: boolean;
  timezone?: string;
  recurrence?: CalendarEventRecurrence;
  recurrenceRule?: string;
  recurrenceEnd?: string;
  attendees?: Attendee[];
  reminders?: Reminder[];
  meetingUrl?: string;
  attachments?: Attachment[];
  color?: string;
  priority?: number;
  isPrivate?: boolean;
  clientId?: string;
  clientIds?: string[];
  orderId?: string;
  reservationId?: string;
  sendInviteEmail?: boolean;
}

export interface UpdateCalendarEventDto {
  title?: string;
  description?: string;
  location?: string;
  type?: CalendarEventType;
  status?: CalendarEventStatus;
  startDate?: string;
  endDate?: string;
  allDay?: boolean;
  timezone?: string;
  recurrence?: CalendarEventRecurrence;
  recurrenceRule?: string;
  recurrenceEnd?: string;
  attendees?: Attendee[];
  reminders?: Reminder[];
  meetingUrl?: string;
  attachments?: Attachment[];
  color?: string;
  priority?: number;
  isPrivate?: boolean;
  clientId?: string;
  clientIds?: string[];
  orderId?: string;
  reservationId?: string;
  sendInviteEmail?: boolean;
}

export interface CalendarEventQueryParams {
  calendarId?: string;
  startDate?: string;
  endDate?: string;
  type?: CalendarEventType;
  status?: CalendarEventStatus;
  clientId?: string;
  page?: number;
  limit?: number;
}

export const calendarService = {
  // ========== Calendar Methods ==========

  async createCalendar(data: CreateCalendarDto): Promise<Calendar> {
    const response = await api.post<Calendar>('/calendar/calendars', data);
    return response.data;
  },

  async getCalendars(): Promise<Calendar[]> {
    const response = await api.get<Calendar[]>('/calendar/calendars');
    return response.data;
  },

  async getCalendarById(id: string): Promise<Calendar> {
    const response = await api.get<Calendar>(`/calendar/calendars/${id}`);
    return response.data;
  },

  async updateCalendar(id: string, data: UpdateCalendarDto): Promise<Calendar> {
    const response = await api.patch<Calendar>(`/calendar/calendars/${id}`, data);
    return response.data;
  },

  async deleteCalendar(id: string): Promise<void> {
    await api.delete(`/calendar/calendars/${id}`);
  },

  // ========== Event Methods ==========

  async createEvent(data: CreateCalendarEventDto): Promise<CalendarEvent> {
    const response = await api.post<CalendarEvent>('/calendar/events', data);
    return response.data;
  },

  async getEvents(params?: CalendarEventQueryParams): Promise<{
    data: CalendarEvent[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await api.get('/calendar/events', { params });
    return response.data;
  },

  async getEventById(id: string): Promise<CalendarEvent> {
    const response = await api.get<CalendarEvent>(`/calendar/events/${id}`);
    return response.data;
  },

  async updateEvent(id: string, data: UpdateCalendarEventDto): Promise<CalendarEvent> {
    const response = await api.patch<CalendarEvent>(`/calendar/events/${id}`, data);
    return response.data;
  },

  async deleteEvent(id: string): Promise<void> {
    await api.delete(`/calendar/events/${id}`);
  },

  // ========== User Calendar Management ==========

  async enableCalendar(): Promise<Calendar> {
    const response = await api.post<Calendar>('/calendar/enable');
    return response.data;
  },

  async disableCalendar(): Promise<void> {
    await api.post('/calendar/disable');
  },

  // ========== Calendar Sync Methods ==========

  async getSyncStatus(): Promise<CalendarSyncStatus> {
    const response = await api.get<CalendarSyncStatus>('/calendar/sync/status');
    return response.data;
  },

  async getGoogleAuthUrl(): Promise<{ authUrl: string }> {
    const response = await api.get<{ authUrl: string }>('/calendar/sync/google/auth');
    return response.data;
  },

  async connectAppleCalendar(data: AppleCalendarCredentials): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/calendar/sync/apple/connect', data);
    return response.data;
  },

  async disconnectGoogle(): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>('/calendar/sync/google/disconnect');
    return response.data;
  },

  async disconnectApple(): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>('/calendar/sync/apple/disconnect');
    return response.data;
  },

  async manualSync(): Promise<SyncResult> {
    const response = await api.post<SyncResult>('/calendar/sync/manual');
    return response.data;
  },
};

// ========== Sync Types ==========

export interface CalendarSyncStatus {
  google: {
    connected: boolean;
    calendarId: string | null;
    lastSync: string | null;
  };
  apple: {
    connected: boolean;
    calendarUrl: string | null;
    username: string | null;
  };
  syncEnabled: boolean;
  calendarId: string | null;
  recentLogs: SyncLog[];
}

export interface SyncLog {
  id: string;
  provider: string;
  action: string;
  direction: string;
  status: string;
  errorMessage?: string;
  createdAt: string;
}

export interface AppleCalendarCredentials {
  calendarUrl: string;
  username: string;
  password: string;
}

export interface SyncResult {
  google: {
    synced: boolean;
    error: string | null;
  };
  apple: {
    synced: boolean;
    error: string | null;
  };
}
